import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import ProductGrid from './ProductGrid';
import { fetchProductDetails, fetchSimilarProducts } from '../../../redux/slices/productsSlice';
import { addToCart, openCart } from '../../../redux/slices/cartSlice'; // Add openCart
import { Truck } from "lucide-react";


const COLOR_MAP = {
  black: '#000000',
  white: '#ffffff',
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#facc15',
  gray: '#64748b',
  pink: '#f472b6',
  beige: '#f5f5dc',
  navy: '#000080',
  blue: '#2563eb',
  lightblue: '#add8e6',
  'light blue': '#add8e6',
  'Navy Blue': '#081820',
  'Petrol Blue': '#0c3238',
  'Royal Blue': '#141c45',
  'Olive Green': '#2e2e0d',
  'Golden Yellow': '#ffa100',
  'Mustard Yellow': '#cf9307',
  'Bottle Green': '#0a3818',
  'Choral': '#9e413e',
  'Grey Melange': '#cccac9',
  'sunset saga': '#FD9C79',
  'urban forest': '#184A45',
  'midnight muse': '#232946',
  'cotton cloud': '#E9EAEC',
  
  brown: '#8B4513',
  lavender: '#E6E6FA',
  // Multi-color combinations (gradients) - exact MongoDB format
  'White Black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'Brown Black': 'linear-gradient(45deg, #8B4513 50%, #000000 50%)',
  'White Lavender': 'linear-gradient(45deg, #ffffff 50%, #E6E6FA 50%)',
  // Also support lowercase versions for flexibility
  'white black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'brown black': 'linear-gradient(45deg, #8B4513 50%, #000000 50%)',
  'white lavender': 'linear-gradient(45deg, #ffffff 50%, #E6E6FA 50%)',
};

const sizeCompliments = {
  XS: 'XS? Extra sleek, extra stunning!',
  S: "S stands for Supreme Style! You're going to own it.",
  M: "M means 'Masterpiece.' Perfect choice for a perfect fit.",
  L: "L is for Limitless Style—you're born to make a statement.",
  XL: "Extra Large, Extra Legendary—you're a total trendsetter!",
  '2XL': "Double the size, double the power—own it!",
  '3XL': "Crafted for comfort and charisma, just like you.",
};

// Haptic feedback helper function
const triggerHaptic = (type = 'medium') => {
  if ('vibrate' in navigator) {
    // Different vibration patterns for different feedback types
    const patterns = {
      light: 10,
      medium: 20,
      heavy: 30,
      error: [20, 10, 20], // Double tap for errors
      success: [10, 5, 15], // Escalating for success
    };
    navigator.vibrate(patterns[type] || patterns.medium);
  }
};

const ProductDetails = ({ productId }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedProduct, loading, error, similarProducts } = useSelector(
    (state) => state.products
  );
  const { user, guestId } = useSelector((state) => state.auth);
  const [mainImage, setMainImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const productFetchId = productId || id;

  useEffect(() => {
    if (productFetchId) {
      dispatch(fetchProductDetails(productFetchId));
      dispatch(fetchSimilarProducts({ id: productFetchId }));
    }
  }, [dispatch, productFetchId]);

  useEffect(() => {
    if (selectedProduct?.images?.length > 0) {
      setMainImage(selectedProduct.images[0].url);
    }
    setSelectedColor('');
    setSelectedSize('');
    setQuantity(1);
    setAddedToCart(false);
    setShowScrollTop(false);
  }, [selectedProduct]);

  useEffect(() => {
    const handleScroll = () => {
      if (addedToCart && window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [addedToCart]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getSwatchColor = (color) => {
    if (COLOR_MAP[color]) {
      return COLOR_MAP[color];
    }
    const normalizedColor = color?.toLowerCase();
    return COLOR_MAP[normalizedColor] || color;
  };

  const isMultiColor = (color) => {
    const colorValue = COLOR_MAP[color] || COLOR_MAP[color?.toLowerCase()];
    return colorValue && colorValue.includes('gradient');
  };

  const handleQuantityChange = (action) => {
    if (action === 'plus') setQuantity((prev) => prev + 1);
    else if (action === 'minus' && quantity > 1) setQuantity((prev) => prev - 1);
  };

  const canAddToCart = () => {
    if (!selectedProduct) return false;
    if (selectedProduct.colors?.length === 1) {
      return !!selectedSize;
    } else {
      return !!selectedSize && !!selectedColor;
    }
  };

  const handleAddToCart = () => {
    if (!canAddToCart()) {
      // Trigger error haptic feedback
      triggerHaptic('error');
      
      toast.error(
        selectedProduct.colors?.length === 1
          ? 'Please select a size.'
          : 'Please select both size and color.',
        { 
          duration: 1000,
          style: {
            background: 'white',
            color: 'black',
            border: 'none',
          }
        }
      );
      return;
    }
    setIsButtonDisabled(true);
    dispatch(
      addToCart({
        productId: productFetchId,
        quantity,
        size: selectedSize,
        color: selectedProduct.colors?.length === 1
          ? selectedProduct.colors[0]
          : selectedColor,
        guestId,
        userId: user?._id,
      })
    )
      .then(() => {
        // Trigger success haptic feedback
        triggerHaptic('success');
        
        toast.success('Product Added To Cart!', { 
          duration: 1000,
          style: {
            background: 'white',
            color: 'black',
            border: 'none',
          }
        });
        setAddedToCart(true);
      })
      .finally(() => {
        setIsButtonDisabled(false);
      });
  };

  // NEW: Handle opening cart
  const handleViewCart = () => {
    dispatch(openCart());
    scrollToTop();
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center text-lg text-gray-400">
        Loading...
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center text-red-600 font-semibold py-6">
        Error: {error}
      </div>
    );
  }
  if (!selectedProduct) {
    return null;
  }

  return (
    <div className="max-w-5xl mt-8 mx-auto px-4 py-8 relative">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

        {/* Image Section */}
        <div>
          {mainImage ? (
            <img
              src={mainImage}
              alt={selectedProduct.name}
              className="rounded-xl w-full object-cover shadow-md"
            />
          ) : (
            <div className="bg-gray-100 h-96 w-full rounded" />
          )}
          <div className="mt-3 flex space-x-2">
            {selectedProduct.images?.map((img, idx) => (
              <img
                key={img.url}
                src={img.url}
                alt={img.altText || `Product ${idx + 1}`}
                className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 ${
                  mainImage === img.url
                    ? 'border-black'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                onClick={() => setMainImage(img.url)}
              />
            ))}
          </div>
        </div>

        {/* Product Info Section */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{selectedProduct.name}</h1>
          <div className="text-xl font-semibold text-gray-900 mb-1">
            ₹{selectedProduct.price}
            {selectedProduct.originalPrice && (
              <span className="ml-3 text-gray-400 line-through text-lg">
                ₹{selectedProduct.originalPrice}
              </span>
            )}
          </div>

          {/* Description with justified text */}
          <div className="mb-6 text-gray-700" style={{ textAlign: 'justify' }}>
            {Array.isArray(selectedProduct.description)
              ? selectedProduct.description.map((desc, idx) => (
                  <p key={idx} className="mb-2">
                    {desc}
                  </p>
                ))
              : (
                <p>{selectedProduct.description}</p>
              )}
          </div>

          {/* Color Selection */}
          {selectedProduct.colors && selectedProduct.colors.length > 0 && (
            <div className="mb-5">
              <div className="text-sm font-semibold mb-2">Color:</div>
              <div className="flex flex-wrap gap-2">
                {selectedProduct.colors.map((color) => {
                  const colorValue = getSwatchColor(color);
                  const isGradient = isMultiColor(color);
                  
                  return (
                    <button
                      key={color}
                      type="button"
                      aria-label={color}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 ${
                        selectedColor === color
                          ? 'ring-2 ring-black ring-offset-1 border-black'
                          : 'hover:border-gray-500 border-gray-300'
                      }`}
                      onClick={() => setSelectedColor(color)}
                      disabled={selectedProduct.colors.length === 1}
                    >
                      <div
                        className="w-6 h-6 rounded border flex items-center justify-center"
                        style={{
                          background: colorValue,
                          border: ['white', 'cotton cloud', 'light blue', 'lightblue'].includes(
                            color.toLowerCase()
                          )
                            ? '2px solid #bbb'
                            : '1px solid #ddd',
                        }}
                        title={color}
                      >
                        {selectedColor === color && (
                          <span 
                            className={`block w-2 h-2 rounded-full border-2 ${
                              isGradient || color.toLowerCase() === 'white' || color.toLowerCase() === 'light blue'
                                ? 'bg-black border-white'
                                : 'bg-white border-black'
                            }`} 
                          />
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-800">{color}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
            <div className="mb-5">
              <div className="text-sm font-semibold mb-2">Size:</div>
              <div className="flex flex-wrap gap-2">
                {selectedSize ? null : <span className="mr-2">Please select a size.</span>}
                {selectedProduct.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`px-4 py-1 rounded-md border text-sm font-semibold ${
                      selectedSize === size
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-800 border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
                {selectedSize && (
                  <span className="px-2 ml-2 py-1 bg-gray-100 rounded text-xs text-gray-800">
                    {selectedSize} - {sizeCompliments[selectedSize]}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Quantity selector */}
          <div className="mb-5">
            <div className="text-sm font-semibold mb-2">Quantity:</div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleQuantityChange('minus')}
                className="w-8 h-8 rounded-md border border-gray-400 bg-black text-white font-bold hover:bg-gray-100 hover:text-black"
                disabled={quantity <= 1}
              >
                −
              </button>
              <span className="font-bold text-lg">{quantity}</span>
              <button
                type="button"
                onClick={() => handleQuantityChange('plus')}
                className="w-8 h-8 rounded-md border border-gray-400 bg-black text-white font-bold hover:bg-gray-100 hover:text-black"
              >
                +
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 text-gray-700 text-sm font-medium">
            <Truck className="w-5 h-5 text-black" />
            <span>Free Shipping </span>
          </div>

          {/* Add to Cart / View Cart Button */}
          <button
            onClick={addedToCart ? handleViewCart : handleAddToCart}
            className="w-full mt-3 bg-black text-white rounded-lg font-semibold px-6 py-3 hover:bg-gray-900 transition disabled:opacity-50"
            disabled={!addedToCart && (isButtonDisabled || selectedProduct.countInStock === 0)}
          >
            {selectedProduct.countInStock === 0 
              ? 'Out of Stock' 
              : addedToCart 
                ? 'View Cart' 
                : 'Add to Cart'
            }
          </button>

          {/* Out of Stock Newsletter Subscription */}
          {selectedProduct.countInStock === 0 && (
            <div className="mt-3 text-center">
              <p className="text-sm text-gray-600 mb-2">Want to be notified when this item is back?</p>
              <button
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                className="text-black underline text-sm font-medium hover:text-gray-700 transition-colors"
              >
                Subscribe to our newsletter for restock alerts
              </button>
            </div>
          )}

          {/* Added to cart message */}
          {addedToCart && (
            <div className="mt-2 text-center text-gray-600 text-sm">
              Product added to cart. Click "View Cart" to checkout.
            </div>
          )}

          {/* Brand and Material info */}
          <div className="mt-8 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Brand</span>
              <span className="text-gray-900">{selectedProduct.brand}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Material</span>
              <span className="text-gray-900">{selectedProduct.material}</span>
            </div>
          </div>

        </div>
      </div>

      {/* Similar Products Section */}
      {similarProducts && similarProducts.length > 0 && (
        <div className="mt-14">
          <hr className="mb-8" />
          <h2 className="text-2xl font-bold mb-5">You might also like</h2>
          <ProductGrid products={similarProducts} />
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-black text-white rounded-full shadow-lg hover:bg-gray-800 transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
          aria-label="Scroll to top"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ProductDetails;
